/**
 * Smart download utility for cross-platform image downloads
 * Handles Telegram WebApp, mobile browsers, and desktop browsers
 */

// Import Telegram types for global Window interface
import '../types/telegram';
import { INTERNAL_BANNER_WIDTH, INTERNAL_BANNER_HEIGHT } from '../constants';

export interface DownloadResult {
  success: boolean;
  method: 'telegram' | 'share' | 'download' | 'preview';
  error?: string;
  message?: string;
}

export interface DownloadOptions {
  onPreviewRequired?: (dataUrl: string, filename: string) => void;
  onTelegramSending?: () => void;
  onTelegramSent?: () => void;
  /** Force standard download, skip Telegram/share logic */
  forceDownloadOnly?: boolean;
}

/**
 * Check if running inside Telegram WebApp
 */
export function isTelegramWebApp(): boolean {
  return !!(
    typeof window !== 'undefined' &&
    window.Telegram?.WebApp?.initData
  );
}

/**
 * Get Telegram user's chat ID for sending images
 */
export function getTelegramChatId(): number | null {
  if (!isTelegramWebApp()) return null;
  return window.Telegram?.WebApp?.initDataUnsafe?.user?.id ?? null;
}

/**
 * Check if running on a mobile device
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Check if Web Share API is available and supports files
 */
export function canUseWebShare(): boolean {
  if (typeof navigator === 'undefined') return false;
  // Check if share and canShare exist as functions
  return typeof navigator.share === 'function' && typeof navigator.canShare === 'function';
}

/**
 * Convert blob to data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Send image directly to user via Telegram bot
 */
async function sendViaTelegramBot(
  blob: Blob,
  filename: string,
  chatId: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const formData = new FormData();
    formData.append('chat_id', chatId.toString());
    formData.append('image', blob, filename);
    formData.append('filename', filename);
    formData.append('caption', `Here's your ${filename.includes('banner') ? 'banner' : filename.includes('meme') ? 'meme' : 'image'}!`);

    // Use relative URL for Vercel deployment
    const response = await fetch('/api/send-telegram-image', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();

    if (!response.ok || result.error) {
      return { success: false, error: result.error || 'Failed to send image' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending via Telegram bot:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Try to share the image using Web Share API
 */
async function tryWebShare(blob: Blob, filename: string): Promise<boolean> {
  if (!canUseWebShare()) return false;

  try {
    const file = new File([blob], filename, { type: blob.type });

    if (!navigator.canShare({ files: [file] })) {
      return false;
    }

    await navigator.share({
      files: [file],
      title: filename.replace(/\.[^/.]+$/, ''), // Remove extension for title
    });

    return true;
  } catch (error) {
    // User cancelled or share failed
    if (error instanceof Error && error.name === 'AbortError') {
      // User cancelled - still consider this "handled"
      return true;
    }
    console.warn('Web Share failed:', error);
    return false;
  }
}

/**
 * Standard download via anchor element
 */
function standardDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = filename;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Smart download function that handles various environments
 *
 * Priority order for Telegram:
 * 1. Send directly via Telegram bot (best UX - image appears in chat)
 * 2. Web Share API fallback
 * 3. Preview fallback (show image for manual save)
 *
 * For non-Telegram:
 * 1. Web Share API (mobile)
 * 2. Standard anchor download (desktop)
 */
export async function downloadImage(
  blob: Blob,
  filename: string,
  options?: DownloadOptions
): Promise<DownloadResult> {
  // If forceDownloadOnly is set, skip all smart logic and just download
  if (options?.forceDownloadOnly) {
    try {
      standardDownload(blob, filename);
      return { success: true, method: 'download' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Download failed';
      return { success: false, method: 'download', error: message };
    }
  }

  const inTelegram = isTelegramWebApp();
  const onMobile = isMobileDevice();

  // Strategy 1: If in Telegram, try to send directly via bot
  if (inTelegram) {
    const chatId = getTelegramChatId();

    if (chatId) {
      options?.onTelegramSending?.();

      const result = await sendViaTelegramBot(blob, filename, chatId);

      if (result.success) {
        options?.onTelegramSent?.();
        return {
          success: true,
          method: 'telegram',
          message: 'Image sent to your Telegram!',
        };
      }

      // Bot sending failed, continue to fallbacks
      console.warn('Telegram bot send failed:', result.error);
    }
  }

  // Strategy 2: Try Web Share API for Telegram fallback and mobile
  if (inTelegram || onMobile) {
    const shareSuccess = await tryWebShare(blob, filename);
    if (shareSuccess) {
      return { success: true, method: 'share' };
    }
  }

  // Strategy 3: Standard download for desktop (non-Telegram)
  if (!inTelegram) {
    try {
      standardDownload(blob, filename);
      return { success: true, method: 'download' };
    } catch (error) {
      console.warn('Standard download failed:', error);
    }
  }

  // Strategy 4: Preview fallback - show image for manual save
  // This is used when in Telegram and other methods fail
  if (options?.onPreviewRequired) {
    try {
      const dataUrl = await blobToDataUrl(blob);
      options.onPreviewRequired(dataUrl, filename);
      return { success: true, method: 'preview' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, method: 'preview', error: message };
    }
  }

  // Last resort: try standard download anyway
  try {
    standardDownload(blob, filename);
    return { success: true, method: 'download' };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Download failed';
    return { success: false, method: 'download', error: message };
  }
}

/**
 * Converts a canvas to a Blob using the standardized internal resolution.
 * This ensures the exported image is crisp and matches the intended banner specifications.
 */
export async function getCanvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  // Use a temporary canvas to ensure the exact internal resolution is used for export.
  // This guarantees that even if the source canvas was resized, the output is consistent.
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = INTERNAL_BANNER_WIDTH;
  exportCanvas.height = INTERNAL_BANNER_HEIGHT;
  
  const ctx = exportCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context for export');
  }

  // Draw the source canvas onto our export canvas.
  // We use high-quality image smoothing for the best results.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, INTERNAL_BANNER_WIDTH, INTERNAL_BANNER_HEIGHT);

  return new Promise((resolve, reject) => {
    exportCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to generate image blob from canvas'));
        }
      },
      'image/png'
    );
  });
}

/**
 * High-level function to download or share a canvas directly.
 * Handles the conversion to blob at the standardized resolution and platform-specific logic.
 */
export async function downloadCanvas(
  canvas: HTMLCanvasElement,
  filename: string,
  options?: DownloadOptions
): Promise<DownloadResult> {
  try {
    const blob = await getCanvasBlob(canvas);
    return await downloadImage(blob, filename, options);
  } catch (error) {
    console.error('Error during canvas export:', error);
    return {
      success: false,
      method: 'download',
      error: error instanceof Error ? error.message : 'Export failed',
    };
  }
}
